import { ColorPalette, StarLarge } from '@griply/assets';
import { IconFormNames } from '@griply/types';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { ColorPickerFormSelect } from 'shared/components/ui/color-picker';
import { Size as SelectSize } from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { FormFieldIcon } from 'shared/components/ui/form-field-icon';
import { FormIcon } from 'shared/components/ui/form-icon';
import {
  ResizableTextArea,
  Variants as ResizableTextAreaVariants,
} from 'shared/components/ui/resizable-text-area';
import { Toggle } from 'shared/components/ui/toggle';
import { LifeAreaFormFields } from 'shared/types/life-area-form';

import * as Styled from './life-area-form.style';
import { useLifeAreaForm } from './use-life-area-form';

const defaultInitialValues: LifeAreaFormFields = {
  iconName: IconFormNames.PlaceholderIcon,
};

export type LifeAreaFormProps = {
  initialValues?: LifeAreaFormFields;
  onSubmit: (lifeArea: LifeAreaFormFields) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onCancel: () => void;
  isEdit?: boolean;
  isCustomIconAllowed?: boolean;
  onPremium?: () => void;
};

export const LifeAreaForm: React.FC<LifeAreaFormProps> = ({
  initialValues,
  onSubmit,
  onDirtyChange,
  onCancel,
  isEdit,
  isCustomIconAllowed,
  onPremium,
}) => {
  const { t } = useTranslation();
  const {
    control,
    onSubmit: submitForm,
    isValid,
    isDirty,
    watch,
    setValue,
    setFocus,
  } = useLifeAreaForm(onSubmit, {
    ...defaultInitialValues,
    ...(initialValues ?? {}),
  });

  const colorValue = watch<'color'>('color');
  const onChangeColor = (color?: string) =>
    setValue<'color'>('color', color ?? null, { shouldDirty: true });

  const iconValue = watch<'iconName'>('iconName');
  const onChangeIcon = (iconName: IconFormNames) =>
    setValue<'iconName'>('iconName', iconName, { shouldDirty: true });

  const favorite = watch<'favorite'>('favorite');
  const onChangeFavorite = () =>
    setValue<'favorite'>('favorite', !favorite, { shouldDirty: true });

  useEffect(() => {
    if (!isEdit) {
      setFocus('name');
    }
  }, [isEdit, setFocus]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <Styled.Form noValidate onSubmit={submitForm}>
      <Styled.ScrollArea>
        <Styled.Header>
          <FormIcon
            value={iconValue}
            onChange={onChangeIcon}
            isPremium={!isCustomIconAllowed}
            onPremium={onPremium}
            large
          />

          <Styled.TitleColumn>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <ResizableTextArea
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  variant={ResizableTextAreaVariants.Subtitle1}
                  placeholder={t('forms.life-area.fields.name.placeholder')}
                  fullWidth
                />
              )}
            />
          </Styled.TitleColumn>
        </Styled.Header>

        <Styled.Body>
          <Divider />

          <Styled.Section>
            <Styled.Item>
              <Styled.Label>
                <FormFieldIcon icon={ColorPalette} />
                <Styled.LabelText>
                  {t('forms.life-area.fields.color.label')}
                </Styled.LabelText>
              </Styled.Label>
              <ColorPickerFormSelect
                placeholder={t('forms.life-area.fields.color.placeholder')}
                onChange={onChangeColor}
                color={colorValue ?? undefined}
                size={SelectSize.ExtraSmall}
                canClear
              />
            </Styled.Item>

            <Styled.Item $autoWidthField>
              <Styled.Label>
                <FormFieldIcon icon={StarLarge} />
                <Styled.LabelText>
                  {t('forms.life-area.fields.favorite.label')}
                </Styled.LabelText>
              </Styled.Label>
              <Toggle
                value="favorite"
                checked={!!favorite}
                onChange={onChangeFavorite}
              />
            </Styled.Item>
          </Styled.Section>
        </Styled.Body>
      </Styled.ScrollArea>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={Sizes.Medium}
          onClick={onCancel}
          color={Colors.TextPrimary}
          type="button"
        >
          {t('forms.life-area.buttons.cancel.label')}
        </Button>
        <Button size={Sizes.Medium} type="submit" disabled={!isValid}>
          {t('forms.life-area.buttons.save.label')}
        </Button>
      </Styled.Footer>
    </Styled.Form>
  );
};
